import React, { Fragment, useState } from 'react';
import { TbCircleCheckFilled, TbZoom } from 'react-icons/tb';
import { useStoreContext } from '../../../Contexts/StoreContext';
import { useCartContext } from '../../../Contexts/CartContext';
import ButtonPrimary from '../../Form/ButtonPrimary';
import Button from '../../Form/Button';
import { MdOutlineTableRestaurant } from 'react-icons/md';
import { IMaskInput } from 'react-imask';
import { normalizeStringToSearch } from '../../../Utils/Helpers';
import Badge from '../Badge';
import { useCartHook } from '../../../Hooks/CartHook';
import BoxContentEndScrollbars from '../BoxContentEndScrollbars';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import ButtonClose from '../../Form/ButtonClose';
import { useTranslation } from 'react-i18next';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { ImSpinner8 } from 'react-icons/im';

interface TableChoiceProps {
    title: string,
    subtitle: string,
    onShowConfirmTable?: CallableFunction;
}

const TableChoice = ({
    title,
    subtitle,
    onShowConfirmTable,
}: TableChoiceProps) => {

    const { t } = useTranslation();
    const { tables, table, setTable, requiredTable, loadingTables } = useStoreContext();
    const { isDinein, isStoreCounter } = useOrderTypeContext();
    const { cartDisplay, setCartDisplay, setShowConfirmTable, currentCart } = useCartContext();
    const { setEnabledCartLoad } = useLayoutContext();
    const [currentTable, setCurrentTable] = useState<any | null>(currentCart?.table ?? null);
    const [searchTerm, setSearchTerm] = useState("");
    const { handleSend, handleUpdateCartTable, handleUpdateCartTableStoreCounter } = useCartHook();

    const handleChangeTable = (table: any) => {
        setCurrentTable(table);
        setEnabledCartLoad(false); //* utilizado para não buscar novamente no cartConference

        if (cartDisplay && isStoreCounter) {
            setCartDisplay((prevState: any) => ({
                ...prevState,
                table: {
                    ...prevState.table,
                    id: table.id,
                    name: table.name
                }
            }));

            //* Usado na abertura da comanda quando não tem mesa informada
            if (requiredTable) {
                handleUpdateCartTableStoreCounter(table);
            } else {
                setShowConfirmTable(false);
            }
        } else {
            if (isDinein) {
                handleUpdateCartTable(table.id);
            }
            setTable(table.id);
        }
    }

    type Table = { name: string };
    type Category = { tables: Table[] };

    const filterTables = (tables: Category[] | null | undefined, searchTerm: string) => {
        if (!tables || !Array.isArray(tables)) return [];

        return tables.reduce((filtered: Category[], category) => {
            const filteredTables = category.tables.filter((table) =>
                normalizeStringToSearch(table.name).includes(normalizeStringToSearch(searchTerm))
            );
            if (filteredTables.length > 0) {
                filtered.push({ ...category, tables: filteredTables });
            }
            return filtered;
        }, []);
    };

    const filteredTables = filterTables(tables, searchTerm);

    return (
        <>
            {(cartDisplay && !requiredTable) &&
                <ButtonClose fixedOnTop onClick={() => onShowConfirmTable!(false)} />
            }
            <div className='flex h-full flex-col justify-between animate-fade-in'>
                <div className='p-4 pt-6 flex flex-col items-center sticky top-0 z-20 bg-white/85 backdrop-blur-sm'>
                    <MdOutlineTableRestaurant size={40} className='zoomInIcon text-shopfood-title' />
                    <div className='flex flex-col w-full gap-2 mt-1'>
                        <span className="font-extrabold w-full text-center text-2xl leading-6 text-shopfood-title">{ title }</span>
                        {subtitle && <span className='text-center text-gray-500 text-base leading-none'>{ subtitle }</span>}

                        <div className="relative flex items-center mt-2">
                            <TbZoom className="pointer-events-none absolute left-3 h-5 w-5 text-gray-400" aria-hidden="true"/>
                            <IMaskInput
                                id="searchTerm"
                                name="searchTerm"
                                mask={/^[a-zA-ZÀ-ÿ0-9\s]*$/}
                                value={searchTerm}
                                onAccept={(value) => setSearchTerm(value)}
                                unmask={true} // true|false|'typed'
                                placeholder={t('store_counter.table_choice.filter_placeholder')}
                                className="block w-full rounded border-0 p-2 pl-9 ring-1 ring-gray-300 placeholder:font-normal placeholder:text-sm placeholder:text-gray-400 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                            />
                            {(cartDisplay?.table?.name || currentCart?.table?.name) &&
                                <Badge type={'custom'} className="text-nowrap absolute right-3 font-extrabold text-xs">
                                    {cartDisplay?.table?.name || currentCart?.table?.name}
                                </Badge>
                            }
                        </div>

                    </div>
                </div>

                <div className='relative h-full flex flex-col min-h-24'>
                    <div className="p-4 h-full" id='scrollContent_tableChoice'>

                        {loadingTables
                            ?
                                <div className='flex w-full h-full items-center justify-center gap-1'><ImSpinner8 className="animate-spin" /><span>{t('layout.loading')}</span></div>
                            :   <>
                                    {filteredTables?.map((tableCategory: any, index: number) => (
                                        <Fragment key={index} >
                                            <div className={`text-lg font-extrabold flex items-center gap-4 ${index > 0 && 'mt-6'} mb-3`}>
                                                <hr className='w-full' />
                                                <span className='text-nowrap leading-5 text-custom-primary'>{tableCategory.name}</span>
                                                <hr className='w-full' />
                                            </div>
                                            <div className='grid grid-cols-3 gap-2 w-full text-sm'>
                                                {tableCategory.tables.map((table: any, index: number) =>
                                                    <Fragment key={index}>
                                                        {   (parseInt(table.id) === parseInt(cartDisplay?.table?.id))
                                                            || (parseInt(table.id) === currentTable?.id)
                                                            || (currentCart?.table?.id === table.id && currentTable?.id === table.id)
                                                            ?   <ButtonPrimary outline onClick={() => setShowConfirmTable(false)} className='relative rounded-md w-full'>
                                                                    <TbCircleCheckFilled size={18} className='absolute -right-1.5 -top-1.5 rounded-full' />
                                                                    {table?.name}
                                                                </ButtonPrimary>
                                                            :   <Button onClick={() => handleChangeTable(table)} className='w-full rounded-md'>{ table.name }</Button>
                                                        }
                                                    </Fragment>
                                                )}
                                            </div>
                                        </Fragment>
                                    ))}
                                    <BoxContentEndScrollbars className='mt-6' message={t('store_counter.table_choice.end_of_list')} />
                                </>
                        }
                    </div>

                    {(cartDisplay?.table?.id && (cartDisplay?.table || table) && !requiredTable) &&
                        <div className='flex w-full gap-2 justify-center p-4 sticky bottom-0 bg-white/65 backdrop-blur-sm'>
                            <ButtonPrimary outline onClick={() => setShowConfirmTable(false)} className='w-full'>
                                {t('store_counter.table_choice.button_cancel_label')}
                            </ButtonPrimary>
                            <ButtonPrimary
                                onClick={handleSend} className='w-full'
                                disabled={(!cartDisplay?.table && !table) || !cartDisplay.table.id}
                                >
                                {t('store_counter.table_choice.button_confirm_label')}
                            </ButtonPrimary>
                        </div>
                    }

                </div>

            </div>
        </>
    );
};

export default TableChoice;
